import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { getFileURL } from '../config/apiClient';

interface AttachmentFile {
  name: string;
  url: string;
  type: string;
  size: number;
}

/**
 * Generate PDF from HTML element with support for images and multi-page layouts
 */
export const generatePDFFromElement = async (
  elementId: string,
  filename: string,
  options?: {
    orientation?: 'portrait' | 'landscape';
    format?: string;
    margin?: number;
  },
  attachments?: AttachmentFile[]
): Promise<void> => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with id "${elementId}" not found`);
  }

  try {
    // Temporarily show print-only elements for PDF generation
    const printOnlyElements = document.querySelectorAll('.print-only');
    printOnlyElements.forEach(el => {
      (el as HTMLElement).style.display = 'block';
    });

    // Get the orientation and format from options
    const orientation = options?.orientation || 'portrait';
    const format = options?.format || 'a4';
    const margin = options?.margin || 10;

    // Preload all images before converting to canvas
    const images = element.querySelectorAll('img');
    const imagePromises: Promise<void>[] = [];

    images.forEach((img) => {
      imagePromises.push(
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = () => resolve();
            img.onerror = () => resolve(); // Continue even if image fails to load
          }
        })
      );
    });

    await Promise.all(imagePromises);

    // Convert HTML to canvas with optimized settings for smaller file size
    const canvas = await html2canvas(element, {
      scale: 1.5, // Reduced from 2 for smaller file size
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      imageTimeout: 5000,
      windowWidth: 1200, // Constrain width for consistent sizing
      windowHeight: 1600,
    });

    // Calculate dimensions based on canvas
    const pageWidth = orientation === 'portrait' ? 210 : 297; // A4 dimensions in mm
    const pageHeight = orientation === 'portrait' ? 297 : 210;
    const imgWidth = pageWidth - 2 * margin;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    // Create PDF
    const pdf = new jsPDF(orientation, 'mm', format);
    let position = margin;

    while (heightLeft > 0) {
      const imgHeightOnPage = Math.min(heightLeft, pageHeight - 2 * margin);
      
      // Calculate the source height based on the page height
      const sourceHeight = (imgHeightOnPage / imgHeight) * canvas.height;
      const sourceStartY = imgHeight - heightLeft > 0 ? (imgHeight - heightLeft) / imgHeight * canvas.height : 0;

      // Create a temporary canvas for this page
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = sourceHeight;
      const ctx = tempCanvas.getContext('2d');
      
      if (ctx) {
        ctx.drawImage(
          canvas,
          0,
          sourceStartY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );

        const pageImgData = tempCanvas.toDataURL('image/jpeg', 0.8); // JPEG with 80% quality instead of PNG
        pdf.addImage(pageImgData, 'JPEG', margin, position, imgWidth, imgHeightOnPage);
      }

      heightLeft -= imgHeightOnPage;

      if (heightLeft > 0) {
        pdf.addPage();
        position = margin;
      }
    }

    // Get the PDF as blob
    const pdfBlob = pdf.output('blob');
    const pdfFilename = filename;

    // Check if there are non-image attachments
    const hasNonImageAttachments = attachments && attachments.some(att => !att.type.startsWith('image/'));

    if (hasNonImageAttachments && attachments && attachments.length > 0) {
      // Create ZIP file with PDF and all attachments
      const zip = new JSZip();
      
      // Add PDF to zip
      zip.file(pdfFilename, pdfBlob);

      // Add all attachments to zip
      for (const attachment of attachments) {
        try {
          const response = await fetch(getFileURL(attachment.url));
          const blob = await response.blob();
          zip.file(attachment.name, blob);
        } catch (error) {
          console.error(`Failed to fetch attachment: ${attachment.name}`, error);
          // Continue with other attachments if one fails
        }
      }

      // Generate ZIP and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = URL.createObjectURL(zipBlob);
      const zipLink = document.createElement('a');
      zipLink.href = zipUrl;
      zipLink.download = filename.replace('.pdf', '.zip');
      document.body.appendChild(zipLink);
      zipLink.click();
      document.body.removeChild(zipLink);
      URL.revokeObjectURL(zipUrl);
    } else {
      // Just download the PDF
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pdfLink = document.createElement('a');
      pdfLink.href = pdfUrl;
      pdfLink.download = pdfFilename;
      document.body.appendChild(pdfLink);
      pdfLink.click();
      document.body.removeChild(pdfLink);
      URL.revokeObjectURL(pdfUrl);
    }

    // Restore print-only elements to hidden
    printOnlyElements.forEach(el => {
      (el as HTMLElement).style.display = 'none';
    });
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Restore print-only elements even if error occurs
    printOnlyElements.forEach(el => {
      (el as HTMLElement).style.display = 'none';
    });
    throw error;
  }
};

/**
 * Download files as attachments in PDF (embedded as links)
 */
export const addAttachmentsToDocument = async (
  files: Array<{ name: string; url: string; type: string }>
): Promise<void> => {
  // Files are handled by the PDF generation through the HTML
  // The HTML should include download links or embedded images
};
